import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { demandes as demandesApi } from '../../services/api';
import referentielsApi from '../../services/referentiels';
import concessionsApi from '../../services/concessions';
import smsApi, { isFixedDomTomNumber } from '../../services/sms';
import aiApi from '../../services/ai';
import { texteTheme } from '../../utils/theme';
import {
  ActionsContact, Bloc, CarteLigne, Feuille, Fiche, Kv, LienOrdinateur, Liste, Pill, Plaque, Puces, Recherche,
  SaisieCommentaire, Segments, Statut, dateFr, depuis, messageErreur, normaliser, quand, useFicheOuverte, useToast,
} from './MobileUI';
import './DemandesMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Demandes clients — version téléphone (2026-10-24).
//
// Le cas d'usage : le call-center saisit la demande (94 % des créations) et
// l'adresse à un collaborateur de concession (réception, magasin), qui la
// TRAITE : il rappelle le client, note un suivi, puis archive avec un motif
// (98 % des archivages). Sur téléphone, il le fait depuis l'atelier ou le
// magasin sans retourner au poste.
//
// Mêmes routes et mêmes règles que LeadsServices.js :
//   - file = ce que renvoie `GET /demandes` (portée `dashboard_scope` et
//     concession appliquées par le serveur), filtrée sur la concession du
//     compte comme sur ordinateur ; urgentes (3 appels ou plus) d'abord ;
//   - masquage RGPD des demandes de plus de 6 mois pour les non-super-admins
//     (nom et coordonnées cachés, contact et SMS coupés) ;
//   - « Nombre d'appels » − / + : `PUT /demandes/:id` ;
//   - SMS : `POST /sms/send` (160 caractères, sans accents, fixes DOM interdits,
//     reformulation `POST /ai/sms-reformuler`), relecture intégrée à la place
//     du window.confirm ;
//   - Atelier : `GET /planning/interventions?search=` puis
//     `GET /planning/interventions/:id/parking`, comme le volet 🔧 Atelier ;
//   - Archiver : les 7 motifs de l'ordinateur, `POST /demandes/:id/archive`.
// Reste sur ordinateur : création, modification complète, Takata, export PDF,
// archives, corbeille, tableau de bord, suivi collaborateurs, call-center IA.
// « Source du contact » n'est enregistrée nulle part : pas reprise ici.
// ═══════════════════════════════════════════════════════════════════════════

// Les 7 motifs d'archivage de l'ordinateur (ArchiveDemandeModal), à l'identique.
const MOTIFS = [
  'Client contacté par téléphone ce jour',
  'Mail envoyé au client',
  'SMS envoyé au client',
  'Client reçu en concession',
  'Sans réponse après relances',
  'Rendez-vous pris',
  'Demande annulée par le client',
];
const SMS_MAX = 160;
const SIX_MOIS_MS = 6 * 30 * 24 * 60 * 60 * 1000;
const URGENCE = 3;

// Même règle que `isRgpdMasked` de LeadsServices.js.
const estMasque = (d, user) => user?.role !== 'super-admin' && d.date_demande
  && Date.now() - new Date(d.date_demande).getTime() > SIX_MOIS_MS;
const nomDemande = (d) => [d.nom, d.prenom].filter(Boolean).join(' ').trim() || 'Sans nom';
const immatNorm = (v) => String(v || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
const tonAppels = (n) => (n >= URGENCE ? 'ko' : n === 2 ? 'attente' : 'ok');
const nomResponsable = (d) => [d.responsable_nom, d.responsable_prenom].filter(Boolean).join(' ') || d.responsable || '';

// Même nettoyage que l'ordinateur (stripAccents) : Mobbiz ne gère pas les accents.
function sansAccents(s) {
  return String(s || '')
    // Apostrophes et guillemets typographiques (clavier iPhone) : ramenés aux
    // caractères simples, sinon « L’équipe » deviendrait « Lequipe ».
    .replace(/[’‘]/g, "'").replace(/[“”]/g, '"')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[œŒ]/g, (m) => (m === 'œ' ? 'oe' : 'OE'))
    .replace(/[æÆ]/g, (m) => (m === 'æ' ? 'ae' : 'AE'))
    .replace(/[ç]/g, 'c').replace(/[Ç]/g, 'C')
    .replace(/[^a-zA-Z0-9 .,;:!?'"()\-\n/€@+]/g, '');
}

function Appels({ n }) {
  return <span className={`mb-pastille mb-ton-${tonAppels(n)}`} aria-label={`${n} appel${n > 1 ? 's' : ''}`}>{n}</span>;
}

export default function DemandesMobile({ user, onVersionOrdinateur }) {
  const portee = user?.dashboard_scope || 'own';
  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [concessions, setConcessions] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [takata, setTakata] = useState(() => new Set());
  const [file, setFile] = useState('toutes');
  const [perimetre, setPerimetre] = useState(null); // 'moi' | 'tous' (portée ≠ own)
  const [recherche, setRecherche] = useState('');
  const [toast, afficher] = useToast();

  const charger = useCallback(async () => {
    try {
      const r = await demandesApi.getAll();
      setDemandes(r.data.demandes || []);
      setErreur('');
    } catch (e) {
      setErreur(messageErreur(e, 'Les demandes n’ont pas pu être chargées.'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
    // Concessions : seulement pour savoir où le SMS est activé (comme l'ordinateur).
    const estGlobal = ['super-admin', 'admin', 'teleoperateur'].includes(user?.role);
    (estGlobal ? concessionsApi.getAll().then((r) => r.data.concessions || [])
      : concessionsApi.getMy().then((r) => (r.data.concession ? [r.data.concession] : [])))
      .then(setConcessions).catch(() => {});
    api.get('/takata/concerned-immats').then((r) => setTakata(new Set(r.data || []))).catch(() => {});
    if (portee !== 'own') {
      referentielsApi.getResponsables().then((r) => setResponsables(r.data.responsables || [])).catch(() => {});
    }
  }, [charger, user, portee]);

  // Pas de flux temps réel pour les demandes (le flux SSE de la page ordinateur
  // ne porte que les appels Ringover entrants) : la liste se recharge au retour
  // sur l'onglet (cas typique : retour après un appel) et chaque minute.
  useEffect(() => {
    const auRetour = () => { if (document.visibilityState === 'visible') charger(); };
    document.addEventListener('visibilitychange', auRetour);
    const minuteur = setInterval(auRetour, 60000);
    return () => { document.removeEventListener('visibilitychange', auRetour); clearInterval(minuteur); };
  }, [charger]);

  const smsActif = useMemo(() => new Set(concessions.filter((c) => c.sms_actif).map((c) => c.id)), [concessions]);
  const monResponsableId = responsables.find((r) => r.user_id === user?.id)?.id;

  // Même filtre de concession que l'ordinateur : celle du compte, s'il en a une.
  const duCompte = useMemo(() => demandes.filter((d) => !user?.concession_id || d.concession_id === user.concession_id),
    [demandes, user]);

  // Portée managed / all : « À moi » par défaut si le compte a des demandes à son nom.
  const aMoi = useCallback((d) => monResponsableId != null && d.responsable_id === monResponsableId, [monResponsableId]);
  const nbAMoi = useMemo(() => duCompte.filter(aMoi).length, [duCompte, aMoi]);
  useEffect(() => {
    if (portee === 'own' || perimetre || chargement || !responsables.length) return;
    setPerimetre(nbAMoi > 0 ? 'moi' : 'tous');
  }, [portee, perimetre, chargement, responsables, nbAMoi]);
  const dansPerimetre = useMemo(() => (portee === 'own' || perimetre !== 'moi' ? duCompte : duCompte.filter(aMoi)),
    [portee, perimetre, duCompte, aMoi]);

  const estTakata = useCallback((d) => !!d.immatriculation && takata.has(immatNorm(d.immatriculation)), [takata]);

  // Puces : Urgentes, Toutes, puis un sujet par sujet présent (du plus fourni au moins fourni).
  const puces = useMemo(() => {
    const parSujet = {};
    dansPerimetre.forEach((d) => { if (d.sujet) parSujet[d.sujet] = (parSujet[d.sujet] || 0) + 1; });
    return [
      { cle: 'urgentes', libelle: 'Urgentes', compte: dansPerimetre.filter((d) => (d.nb_appels || 0) >= URGENCE).length, alerte: true },
      { cle: 'toutes', libelle: 'Toutes', compte: dansPerimetre.length },
      ...Object.entries(parSujet).sort((a, b) => b[1] - a[1]).map(([s, n]) => ({ cle: `s:${s}`, libelle: s, compte: n })),
    ];
  }, [dansPerimetre]);
  // Une puce de sujet qui se vide (dernière demande archivée) ramène à « Toutes ».
  useEffect(() => {
    if (file.startsWith('s:') && !puces.find((p) => p.cle === file)) setFile('toutes');
  }, [file, puces]);

  // Recherche : nom, téléphone, immatriculation. Une demande masquée (RGPD) ne
  // se retrouve que par sa plaque, pour ne pas révéler l'identité par la bande.
  const qn = normaliser(recherche);
  const qImmat = immatNorm(recherche);
  const visibles = useMemo(() => {
    const correspond = (d) => {
      if (!qn) return true;
      if (qImmat.length >= 2 && immatNorm(d.immatriculation).includes(qImmat)) return true;
      if (estMasque(d, user)) return false;
      const chiffres = recherche.replace(/\D/g, '');
      return normaliser(nomDemande(d)).includes(qn) || normaliser(`${d.prenom || ''} ${d.nom || ''}`).includes(qn)
        || (chiffres.length >= 3 && String(d.telephone || '').replace(/\D/g, '').includes(chiffres));
    };
    const base = dansPerimetre.filter((d) => (file === 'urgentes' ? (d.nb_appels || 0) >= URGENCE
      : file.startsWith('s:') ? d.sujet === file.slice(2) : true)).filter(correspond);
    // Urgentes d'abord (nombre d'appels décroissant, comme le serveur), puis la plus ancienne.
    return [...base].sort((a, b) => (b.nb_appels || 0) - (a.nb_appels || 0)
      || new Date(a.created_at) - new Date(b.created_at));
  }, [dansPerimetre, file, qn, qImmat, recherche, user]);

  const cle = useCallback((d) => d.id, []);
  const fiche = useFicheOuverte(visibles, cle);

  const majLocale = (id, champs) => setDemandes((prev) => prev.map((d) => (d.id === id ? { ...d, ...champs } : d)));
  const retirer = (id) => {
    // On passe à la demande suivante de la file plutôt que de revenir à la liste.
    if (fiche.suivant) fiche.suivant(); else if (fiche.precedent) fiche.precedent(); else fiche.fermer();
    setDemandes((prev) => prev.filter((d) => d.id !== id));
  };

  const voitResponsable = portee !== 'own' && perimetre !== 'moi';
  const plusieursConcessions = !user?.concession_id && new Set(duCompte.map((d) => d.concession_id)).size > 1;

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-dem-q" valeur={recherche} onChange={setRecherche} placeholder="Nom, téléphone, immatriculation" />
        {portee !== 'own' && perimetre && (
          <Segments label="Périmètre" valeur={perimetre} onChange={setPerimetre} options={[
            { cle: 'moi', libelle: 'À moi', compte: nbAMoi },
            { cle: 'tous', libelle: portee === 'all' ? 'Toutes' : 'Mon équipe', compte: duCompte.length },
          ]} />
        )}
      </div>
      <Puces label="File" valeur={file} onChange={setFile} options={puces} />

      <Liste
        elements={visibles}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={qn ? 'Aucune demande ne correspond à la recherche.' : file === 'urgentes' ? 'Aucune demande urgente.' : 'Aucune demande à traiter.'}
        groupe={(d) => ((d.nb_appels || 0) >= URGENCE ? 'Urgentes · 3 appels ou plus' : 'À traiter')}
        onOuvrir={fiche.ouvrir}
        carte={(d) => {
          const masque = estMasque(d, user);
          const n = d.nb_appels || 0;
          const heures = (Date.now() - new Date(d.created_at).getTime()) / 3600000;
          return {
            couleur: n >= URGENCE ? 'var(--color-error)' : 'transparent',
            contenu: (
              <>
                <CarteLigne
                  titre={<span className="mb-dem-nom"><Appels n={n} />{masque ? 'Anonymisé (RGPD)' : nomDemande(d)}</span>}
                  age={depuis(d.created_at)} retard={heures > 24} />
                {(d.synthese || d.commentaire_initial) && !masque && (
                  <span className="mb-carte-texte">{d.synthese || d.commentaire_initial}</span>
                )}
                <span className="mb-carte-meta">
                  {d.sujet && <Pill ton="accent">{d.sujet}</Pill>}
                  <Plaque immat={d.immatriculation} />
                  {estTakata(d) && <Pill ton="ko">TAKATA</Pill>}
                  {d.source === 'agent-vocal' && <Pill ton="accent">IA</Pill>}
                  {voitResponsable && nomResponsable(d) && <Pill>{aMoi(d) ? 'À moi' : nomResponsable(d)}</Pill>}
                  {plusieursConcessions && d.concession_nom && <Pill>{d.concession_nom}</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      <LienOrdinateur onClick={onVersionOrdinateur}>Créer une demande, archives, tableau de bord : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheDemande
          key={fiche.ouverte.id}
          demande={fiche.ouverte}
          user={user}
          position={fiche.index}
          total={visibles.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          masque={estMasque(fiche.ouverte, user)}
          smsActif={smsActif.has(fiche.ouverte.concession_id)}
          takata={estTakata(fiche.ouverte)}
          aMoi={aMoi(fiche.ouverte)}
          onMaj={majLocale}
          onRetire={retirer}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FicheDemande({ demande, user, position, total, onRetour, onPrecedent, onSuivant, masque, smsActif, takata, aMoi,
  onMaj, onRetire, afficher, onVersionOrdinateur }) {
  const [commentaires, setCommentaires] = useState(null);
  const [feuille, setFeuille] = useState(null); // 'archiver' | 'sms'
  const [appels, setAppels] = useState(demande.nb_appels || 0);
  const [erreurAppels, setErreurAppels] = useState('');

  const chargerCommentaires = useCallback(() => {
    demandesApi.getCommentaires(demande.id).then((r) => setCommentaires(r.data.commentaires || [])).catch(() => setCommentaires([]));
  }, [demande.id]);
  useEffect(() => { chargerCommentaires(); }, [chargerCommentaires]);

  // Le rafraîchissement de la liste peut apporter un compteur changé ailleurs.
  useEffect(() => { setAppels(demande.nb_appels || 0); }, [demande.nb_appels]);

  // Compteur d'appels : même route que l'ordinateur (PUT complet de la fiche).
  // `responsable` est renvoyé tel quel : le serveur écrit `responsable || ''`
  // et effacerait sinon l'ancien libellé texte du responsable.
  const changerAppels = async (delta) => {
    const avant = appels;
    const apres = Math.max(0, avant + delta);
    if (apres === avant) return;
    setAppels(apres); setErreurAppels('');
    try {
      await demandesApi.update(demande.id, { nb_appels: apres, responsable: demande.responsable || '' });
      onMaj(demande.id, { nb_appels: apres });
    } catch (e) {
      setAppels(avant);
      setErreurAppels(messageErreur(e, 'Le nombre d’appels n’a pas été enregistré.'));
    }
  };

  // Raison écrite en clair quand le SMS est coupé (même ordre que l'ordinateur).
  const raisonSms = masque ? 'Coordonnées anonymisées (RGPD) : SMS impossible.'
    : !demande.telephone ? 'Aucun téléphone : SMS impossible.'
      : isFixedDomTomNumber(demande.telephone) ? 'Numéro fixe DOM (0590 / 0596 / 0594) : SMS interdit.'
        : !smsActif ? 'SMS non activé pour cette concession.' : '';

  const mailto = !masque && demande.email
    ? `mailto:${demande.email}?subject=${encodeURIComponent('Votre demande auprès de nos services')}` : null;
  const recents = (commentaires || []).slice().reverse(); // du plus récent au plus ancien
  // « Demande initiale » sur le seul plus ancien commentaire marqué `is_initial` :
  // en base locale, tous les commentaires portent le drapeau (anomalie de données).
  const idInitial = (commentaires || []).find((c) => c.is_initial)?.id;

  return (
    <Fiche
      titre={`Demande #${demande.id}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={<button type="button" className="mb-bouton mb-bouton--principal" onClick={() => setFeuille('archiver')}>Archiver</button>}
    >
      <div className="mb-entete">
        <div>
          <div className="mb-entete-haut">
            <Plaque immat={demande.immatriculation} />
            {demande.sujet && <span>{demande.sujet}</span>}
            <span>{depuis(demande.created_at)}</span>
          </div>
          <div className="mb-entete-titre">{masque ? 'Client anonymisé (RGPD)' : nomDemande(demande)}</div>
          {[demande.marque, demande.modele].filter(Boolean).length > 0 && (
            <div className="mb-entete-sous">{[demande.marque, demande.modele].filter(Boolean).join(' ')}</div>
          )}
        </div>
        <ActionsContact tel={masque ? '' : demande.telephone} email={masque ? '' : (demande.email || '')} mailto={mailto}
          onSms={() => setFeuille('sms')} smsDesactive={raisonSms || undefined} />
        {masque
          ? <p className="mb-aide">Demande de plus de 6 mois : nom et coordonnées masqués (RGPD).</p>
          : <p className="mb-aide">{[demande.telephone, demande.email].filter(Boolean).join(' · ')}{raisonSms ? ` — ${raisonSms}` : ''}</p>}
        <div className="mb-dem-appels">
          <span className="mb-dem-appels-libelle">Nombre d’appels du client<small>{appels >= URGENCE ? 'Urgente' : 'Urgente à partir de 3'}</small></span>
          <button type="button" aria-label="Un appel de moins" onClick={() => changerAppels(-1)} disabled={appels <= 0}>−</button>
          <Appels n={appels} />
          <button type="button" aria-label="Un appel de plus" onClick={() => changerAppels(1)}>+</button>
        </div>
        {erreurAppels && <p className="mb-aide mb-aide--erreur">{erreurAppels}</p>}
      </div>

      {takata && <p className="mb-aide mb-aide--erreur">Véhicule concerné par la campagne TAKATA (dossier : version ordinateur).</p>}

      {!masque && (demande.synthese || demande.commentaire_initial) && (
        <div className="mb-citation">
          {demande.synthese || demande.commentaire_initial}
          <small>{demande.synthese ? 'Synthèse IA de la demande' : 'Demande saisie'}{demande.source === 'agent-vocal' ? ' · créée par l’agent vocal IA' : ''}</small>
        </div>
      )}

      <Bloc titre="Historique" compte={commentaires ? commentaires.length : null}
        resume={recents[0] ? quand(recents[0].created_at) : ''} ouvert>
        {commentaires === null && <p className="mb-aide">Chargement…</p>}
        {commentaires && commentaires.length === 0 && <p className="mb-aide">Aucun commentaire.</p>}
        {recents.map((c) => (
          <div key={c.id} className="mb-commentaire">
            {masque ? '●●●●●●●●●●' : c.commentaire}
            <small>
              {[c.user_prenom, c.user_nom].filter(Boolean).join(' ') || '—'} · {quand(c.created_at)}
              {c.id === idInitial ? ' · demande initiale' : ''}{c.updated_at && c.created_at && c.updated_at !== c.created_at ? ' · modifié' : ''}
            </small>
          </div>
        ))}
        <SaisieCommentaire id="mb-dem-com" placeholder="Ajouter un commentaire de suivi"
          onEnvoyer={async (t) => { await demandesApi.addCommentaire(demande.id, t); chargerCommentaires(); }} />
      </Bloc>

      <BlocAtelier immatriculation={demande.immatriculation} vin={demande.vin} />

      <Bloc titre="Demande" resume={aMoi ? 'à vous' : nomResponsable(demande)}>
        <Kv lignes={[
          ['Sujet', demande.sujet],
          ['Responsable', nomResponsable(demande) + (demande.responsable_service ? ` (${demande.responsable_service})` : '')],
          ['Concession', demande.concession_nom],
          ['Date', dateFr(demande.date_demande)],
          ['N° BDC', demande.bon_de_commande],
          demande.vin && ['VIN', masque ? '●●●●●●●●●●' : demande.vin],
        ]} />
      </Bloc>

      <p className="mb-aide mb-aide--centre">
        Modifier la demande (client, véhicule, responsable, concession), Takata, export, archives, corbeille,
        tableau de bord, suivi collaborateurs et call-center IA : version ordinateur.
      </p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {feuille === 'archiver' && (
        <FeuilleArchiver demande={demande} onFermer={() => setFeuille(null)}
          onFait={(rapide) => {
            setFeuille(null);
            afficher(rapide ? 'Traité en moins d’1 heure ! Bravo !' : 'Demande archivée');
            onRetire(demande.id);
          }} />
      )}
      {feuille === 'sms' && !raisonSms && (
        <FeuilleSms demande={demande} onFermer={() => setFeuille(null)}
          onEnvoye={() => { setFeuille(null); afficher('SMS envoyé'); chargerCommentaires(); }} />
      )}
    </Fiche>
  );
}

// ── Bloc Atelier : même lecture que le volet 🔧 Atelier de l'ordinateur ──────
function BlocAtelier({ immatriculation, vin }) {
  const [etat, setEtat] = useState({ charge: true, droitRefuse: false, erreur: '', dossiers: [] });
  const [parking, setParking] = useState(null);
  const im = immatNorm(immatriculation);
  const vn = immatNorm(vin);
  const cleVehicule = im.length >= 4 ? im : (vn.length >= 8 ? vn : '');

  useEffect(() => {
    if (!cleVehicule) return undefined;
    let annule = false;
    api.get('/planning/interventions', { params: { search: cleVehicule, inclure_a_planifier: '1' } })
      .then(({ data }) => {
        if (annule) return;
        const brut = Array.isArray(data) ? data : (data.interventions || []);
        // La recherche est floue (ILIKE) : ne garder que le véhicule exact.
        const dossiers = brut.filter((i) => immatNorm(i.immat || i.immatriculation) === cleVehicule || immatNorm(i.vin) === cleVehicule)
          .filter((i) => !i.is_complement);
        setEtat({ charge: false, droitRefuse: false, erreur: '', dossiers });
      })
      .catch((e) => {
        if (annule) return;
        setEtat({ charge: false, droitRefuse: e?.response?.status === 403, erreur: e?.response?.status === 403 ? '' : 'Suivi atelier momentanément indisponible.', dossiers: [] });
      });
    return () => { annule = true; };
  }, [cleVehicule]);

  // Dossier « vivant » : celui d'aujourd'hui, sinon le prochain à venir.
  const auj = new Date();
  const ymd = `${auj.getFullYear()}-${String(auj.getMonth() + 1).padStart(2, '0')}-${String(auj.getDate()).padStart(2, '0')}`;
  const tries = etat.dossiers.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  const aVenir = tries.filter((d) => String(d.date || '') >= ymd);
  const vivant = aVenir.length ? aVenir[aVenir.length - 1] : null;
  const passes = tries.filter((d) => d !== vivant && String(d.date || '') < ymd).slice(0, 5);

  useEffect(() => {
    setParking(null);
    if (!vivant?.id) return undefined;
    let annule = false;
    api.get(`/planning/interventions/${vivant.id}/parking`)
      .then((r) => { if (!annule && r.data?.position) setParking(r.data.position); })
      .catch(() => {});
    return () => { annule = true; };
  }, [vivant?.id]);

  if (!cleVehicule) {
    return <Bloc titre="Atelier" resume="pas de véhicule"><p className="mb-aide">Ni immatriculation ni VIN sur cette demande.</p></Bloc>;
  }
  const libelleStatut = vivant ? (vivant.statut_atelier_nom || (vivant.a_planifier ? 'À planifier' : 'Planifié')) : '';
  const resume = etat.charge ? '…' : etat.droitRefuse ? 'accès Planning requis'
    : vivant ? libelleStatut : etat.dossiers.length ? 'pas de dossier en cours' : 'aucun passage';
  const dateCourte = (v) => (String(v || '').slice(0, 10) === ymd ? 'Aujourd’hui' : dateFr(String(v || '').slice(0, 10)));

  return (
    <Bloc titre="Atelier" resume={resume}>
      {etat.charge && <p className="mb-aide">Recherche…</p>}
      {etat.droitRefuse && <p className="mb-aide">Suivi atelier : accès au Planning requis.</p>}
      {etat.erreur && <p className="mb-aide mb-aide--erreur">{etat.erreur}</p>}
      {!etat.charge && !etat.erreur && !etat.droitRefuse && !etat.dossiers.length && (
        <p className="mb-aide">Aucun passage atelier connu pour ce véhicule.</p>
      )}
      {vivant && (
        <>
          <div><Statut libelle={libelleStatut} couleur={vivant.statut_atelier_couleur} /></div>
          <Kv lignes={[
            ['Date', `${dateCourte(vivant.date)}${vivant.heure_reception ? ` · RDV à ${vivant.heure_reception}` : ''}`],
            ['N° OR', vivant.numero_or || 'à préparer'],
            vivant.mecanicien && ['Mécanicien', vivant.mecanicien],
            vivant.receptionnaire_nom && ['Réceptionnaire', vivant.receptionnaire_nom],
            vivant.heure_restitution_prevue && ['Restitution prévue', vivant.heure_restitution_prevue],
            vivant.restituee_at && ['Restitué', quand(vivant.restituee_at)],
            ['Parking', parking?.zone || ''],
            (vivant.a_courtoisie || vivant.client_attend) && ['À noter', [vivant.a_courtoisie && 'véhicule de courtoisie', vivant.client_attend && 'client attend'].filter(Boolean).join(', ')],
          ]} />
        </>
      )}
      {passes.length > 0 && (
        <>
          <span className="mb-etiquette">Passages précédents</span>
          {passes.map((d) => (
            <div key={d.id} className="mb-commentaire">
              {d.description || d.type_intervention || '—'}
              <small style={d.statut_atelier_couleur && !d.restituee_at ? { color: texteTheme(d.statut_atelier_couleur) } : undefined}>
                {dateCourte(d.date)} · {d.restituee_at ? 'Restitué' : (d.statut_atelier_nom || '—')}{d.numero_or ? ` · OR ${d.numero_or}` : ''}
              </small>
            </div>
          ))}
        </>
      )}
    </Bloc>
  );
}

// ── Archiver : les 7 motifs en grandes puces ────────────────────────────────
function FeuilleArchiver({ demande, onFermer, onFait }) {
  const [motif, setMotif] = useState('');
  const [libre, setLibre] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const choisi = (libre.trim() || motif).trim();
  const archiver = async () => {
    if (!choisi || envoi) return;
    setEnvoi(true); setErreur('');
    try {
      await demandesApi.archive(demande.id, choisi);
      const rapide = demande.created_at && Date.now() - new Date(demande.created_at).getTime() < 3600000;
      onFait(rapide);
    } catch (e) {
      setErreur(messageErreur(e, 'La demande n’a pas pu être archivée.'));
      setEnvoi(false);
    }
  };
  return (
    <Feuille titre="Archiver la demande" onFermer={onFermer} fermer="Annuler">
      <p className="mb-aide">Choisissez ce qui a été fait. La demande part dans les archives avec ce motif.</p>
      <div className="mb-choix">
        {MOTIFS.map((m) => (
          <button key={m} type="button" className={motif === m && !libre.trim() ? 'is-actif' : ''}
            onClick={() => { setMotif(m); setLibre(''); }}>{m}</button>
        ))}
      </div>
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-dem-motif">Ou un autre motif</label>
        <input id="mb-dem-motif" value={libre} onChange={(e) => setLibre(e.target.value)} placeholder="Ex. : client rappelé, tout est ok" />
      </div>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      <div className="mb-feuille-pied">
        <button type="button" className="mb-bouton" onClick={onFermer}>Annuler</button>
        <button type="button" className="mb-bouton mb-bouton--principal" disabled={!choisi || envoi} onClick={archiver}>
          {envoi ? 'Archivage…' : 'Archiver'}
        </button>
      </div>
    </Feuille>
  );
}

// ── SMS : même flux que la SmsModal de l'ordinateur, relecture intégrée ─────
function FeuilleSms({ demande, onFermer, onEnvoye }) {
  const [telephone, setTelephone] = useState(demande.telephone || '');
  const [message, setMessage] = useState('');
  const [brouillon, setBrouillon] = useState(null); // avant reformulation IA
  const [reformulation, setReformulation] = useState(false);
  const [relecture, setRelecture] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [historique, setHistorique] = useState([]);

  useEffect(() => {
    smsApi.getHistory(demande.id).then((r) => setHistorique(r.data.history || [])).catch(() => {});
  }, [demande.id]);

  const fixe = isFixedDomTomNumber(telephone);
  const pret = message.trim() && telephone.trim() && !fixe;

  const reformuler = async () => {
    if (!message.trim() || reformulation) return;
    setErreur(''); setReformulation(true);
    const avant = message;
    try {
      const r = await aiApi.reformulerSms(avant.trim(), demande.id);
      const propose = sansAccents((r.data?.message || '').trim()).slice(0, SMS_MAX);
      if (!propose) throw new Error('Réponse IA vide');
      setBrouillon(avant);
      setMessage(propose);
    } catch (e) {
      setErreur(messageErreur(e, 'Reformulation IA indisponible'));
    } finally {
      setReformulation(false);
    }
  };

  const envoyer = async () => {
    if (!pret || envoi) return;
    setEnvoi(true); setErreur('');
    try {
      await smsApi.send({ demande_id: demande.id, telephone, message: message.trim() });
      onEnvoye();
    } catch (e) {
      setErreur(messageErreur(e, 'Erreur lors de l’envoi du SMS'));
      setRelecture(false);
      setEnvoi(false);
    }
  };

  if (relecture) {
    return (
      <Feuille titre="Relire avant l’envoi" onFermer={() => setRelecture(false)} fermer="Modifier">
        <div className="mb-dem-relecture">
          {message.trim()}
          <small>À {telephone.trim()} · {message.trim().length}/{SMS_MAX} caractères · envoyé depuis le CRM</small>
        </div>
        {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
        <div className="mb-feuille-pied">
          <button type="button" className="mb-bouton" onClick={() => setRelecture(false)} disabled={envoi}>Modifier</button>
          <button type="button" className="mb-bouton mb-bouton--principal" onClick={envoyer} disabled={envoi}>
            {envoi ? 'Envoi…' : 'Envoyer le SMS'}
          </button>
        </div>
      </Feuille>
    );
  }

  return (
    <Feuille titre="Envoyer un SMS" onFermer={onFermer}>
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-dem-sms-tel">Numéro</label>
        <input id="mb-dem-sms-tel" type="tel" inputMode="tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} />
        {fixe && <p className="mb-aide mb-aide--erreur">Numéro fixe DOM (0590 / 0596 / 0594) : l’envoi de SMS est interdit sur ces lignes.</p>}
      </div>
      <div className="mb-champ">
        <div className="mb-rangee">
          <label className="mb-etiquette" htmlFor="mb-dem-sms-msg">Message</label>
          <span className={`mb-compteur${message.length >= SMS_MAX ? ' is-trop' : ''}`}>{message.length}/{SMS_MAX}</span>
        </div>
        <textarea id="mb-dem-sms-msg" className="mb-zone" value={message} maxLength={SMS_MAX}
          onChange={(e) => { setMessage(sansAccents(e.target.value).slice(0, SMS_MAX)); setBrouillon(null); }}
          placeholder="Saisissez votre message…" />
        <p className="mb-aide">160 caractères au plus ; les accents sont retirés (le fournisseur SMS ne les gère pas).</p>
        <div className="mb-rangee">
          <button type="button" className="mb-mini mb-mini--accent" onClick={reformuler} disabled={reformulation || !message.trim()}>
            {reformulation ? 'Reformulation…' : 'Reformuler avec l’IA'}
          </button>
          {brouillon !== null && (
            <button type="button" className="mb-mini" onClick={() => { setMessage(brouillon); setBrouillon(null); }} disabled={reformulation}>
              Restaurer mon brouillon
            </button>
          )}
        </div>
      </div>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      <div className="mb-feuille-pied">
        <button type="button" className="mb-bouton" onClick={onFermer}>Fermer</button>
        <button type="button" className="mb-bouton mb-bouton--principal" disabled={!pret} onClick={() => setRelecture(true)}>Relire et envoyer</button>
      </div>
      {historique.length > 0 && (
        <div className="mb-dem-sms-histo">
          <span className="mb-etiquette">Historique SMS ({historique.length})</span>
          {historique.map((h) => (
            <div key={h.id} className={`mb-commentaire${h.statut === 'erreur' ? ' is-erreur' : ''}`}>
              {h.message}
              <small>
                {[h.user_prenom, h.user_nom].filter(Boolean).join(' ') || '—'} · {quand(h.created_at)}
                {h.statut === 'erreur' ? ` · échec : ${h.erreur || 'erreur'}` : ''}
              </small>
            </div>
          ))}
        </div>
      )}
    </Feuille>
  );
}
